'use no memo';

import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback, useEffect, useMemo } from 'react';
import { FillScreen, HStack, VStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import { Text } from '@/components/Typography/Text';
import { useToast } from '@/components/ui/use-toast';
import { operationInstanceQueryKeys } from '@/features/operation-instances/hooks';
import { stacktraceNS, useTranslation } from '@/lib/i18n';
import { useSuspenseQueryOperationPackageInstance } from '../../-queries';
import { ExecuteTestCasesOperation } from './-components/ExecuteTestCasesOperation';
import { OrganizeIssuesOperation } from './-components/OrganizeIssuesOperation';
import { SelectTargetFunctionsOperation } from './-components/SelectTargetFunctionsOperation';
import { SelectTestCasesOperation } from './-components/SelectTestCasesOperation';
import { useCompleteTestExecution, useGetTestExecutionCondition } from './-query';
import { findAssignmentTaskById, resolveDefaultAssignmentTask, resolveNextInProgressAssignmentTask } from './-utils/resolveAssignmentTask';
import { resolveDefaultExecuteTask } from './-utils/resolveExecuteTask';
import { resolveDefaultOrganizeIssuesTask } from './-utils/resolveOrganizeIssuesTask';
import { resolveDefaultSelectFunctionsTask } from './-utils/resolveSelectFunctionsTask';
import { dataSearchSchema } from './-zod-schema';
export const Route = createFileRoute('/_stacktrace/qa-operations-core/operation-packages/$id/patterns/$patternId/instances/$instanceId/_layout/data/')({
  component: RouteComponent,
  validateSearch: zodValidator(dataSearchSchema)
});

/**
 * QA オペレーションコア インスタンス詳細「データ」タブ。
 *
 * packageType で遂行内容を出し分ける（同一ルートで複数種別を扱う）。
 * - TestExecution: 単一タスク `ExecuteTestCases`（評価入力グリッド）。
 * - TestAssignment: Step1 `SelectTestCases`（テストケースの複数選択グリッド）の
 *   みをデータタブで遂行する。Step2 `AssignExecutor`（アサイン先選択）は詳細タブの
 *   タスクカード内でインライン完了するためデータタブでは扱わない。
 * - IssueReconciliation: Step2 `OrganizeIssues`（不具合一覧の diff 表示 + 完了）。
 *   Step1（アサイン）は詳細タブのタスクカード内でインライン完了する。
 */
function RouteComponent() {
  const params = Route.useParams();
  const {
    instanceId
  } = params;
  const {
    data: instance
  } = useSuspenseQueryOperationPackageInstance(instanceId);
  if (instance.packageType === 'TestAssignment') {
    return <TestAssignmentDataTab />;
  }
  if (instance.packageType === 'TestSpecDesign') {
    return <TestSpecDesignDataTab />;
  }
  if (instance.packageType === 'IssueReconciliation') {
    return <IssueReconciliationDataTab />;
  }
  return <TestExecutionDataTab />;
}

/**
 * 不具合棚卸しデータタブ（Step2 `OrganizeIssues`）。
 *
 * 走行中インスタンスの対象プロダクトに紐づく不具合一覧を表示し、
 * 「不具合の整理を完了」で専用エンドポイントを叩いて完了する。完了後は
 * detail タブへ戻す（PP「価値の棚卸し」のデータタブと同型）。Step1（アサイン）
 * は詳細タブのタスクカード内でインライン完了するためデータタブでは扱わない。
 */
function IssueReconciliationDataTab() {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'qaOperationsCore.issueReconciliation.instance.data'
  });
  const params = Route.useParams();
  const {
    instanceId
  } = params;
  const navigate = Route.useNavigate();
  const {
    data: instance
  } = useSuspenseQueryOperationPackageInstance(instanceId);
  const steps = instance.operationSteps;
  const organizeTask = useMemo(() => resolveDefaultOrganizeIssuesTask(steps), [steps]);
  const handleCompleted = useCallback(() => {
    navigate({
      to: '/qa-operations-core/operation-packages/$id/patterns/$patternId/instances/$instanceId/detail',
      params
    });
  }, [navigate, params]);
  if (!organizeTask) {
    return <FillScreen className="flex items-center justify-center">
        <Text variant="label" className="text-neutral-muted-on-fill">
          {t('noTask')}
        </Text>
      </FillScreen>;
  }
  return <FillScreen>
      <OrganizeIssuesOperation instanceId={instanceId} taskId={organizeTask.task.id} isCompleted={organizeTask.task.status === 'Completed'} onCompleted={handleCompleted} />
    </FillScreen>;
}

/**
 * テスト設計（TestSpecDesign）データタブ（Step2 `SelectTargetFunctions`）。
 *
 * 走行中インスタンスの対象 TestSet が属するテナントの機能ツリーを候補として取得し、
 * kind=FUNCTION のノードを複数選択して保存・完了する（最終タスクのため完了で
 * Completed）。完了後は detail タブへ戻す。
 */
function TestSpecDesignDataTab() {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'qaOperationsCore.testSpecDesign.instance.data'
  });
  const params = Route.useParams();
  const {
    instanceId
  } = params;
  const navigate = Route.useNavigate();
  const {
    data: instance
  } = useSuspenseQueryOperationPackageInstance(instanceId);
  const steps = instance.operationSteps;
  const selectTask = useMemo(() => resolveDefaultSelectFunctionsTask(steps), [steps]);
  const taskId = selectTask?.task.id;
  const isCompleted = selectTask?.task.status === 'Completed';
  const handleCompleted = useCallback(() => {
    navigate({
      to: '/qa-operations-core/operation-packages/$id/patterns/$patternId/instances/$instanceId/detail',
      params
    });
  }, [navigate, params]);
  if (!selectTask || !taskId) {
    return <FillScreen className="flex items-center justify-center">
        <Text variant="label" className="text-neutral-muted-on-fill">
          {t('noTask')}
        </Text>
      </FillScreen>;
  }
  return <FillScreen>
      <SelectTargetFunctionsOperation instanceId={instanceId} taskId={taskId} isCompleted={isCompleted} onCompleted={handleCompleted} />
    </FillScreen>;
}

/**
 * テスト実施データタブ（単一タスク `ExecuteTestCases`）。
 *
 * 走行中インスタンスから対象テスト条件を解決し、評価入力グリッドで各明細を
 * 入力・保存して完了する（専用エンドポイント）。
 */
function TestExecutionDataTab() {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'qaOperationsCore.testExecution.instance.data'
  });
  const params = Route.useParams();
  const {
    instanceId
  } = params;
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const {
    data: instance
  } = useSuspenseQueryOperationPackageInstance(instanceId);
  const steps = instance.operationSteps;
  const executeTask = useMemo(() => resolveDefaultExecuteTask(steps), [steps]);
  const taskId = executeTask?.task.id;
  const isCompleted = executeTask?.task.status === 'Completed';
  const {
    data: condition,
    isPending: isConditionPending,
    isError: isConditionError
  } = useGetTestExecutionCondition(instanceId);
  const {
    mutate: completeTestExecution,
    isPending: isCompleting
  } = useCompleteTestExecution(instanceId, {
    onSuccess: () => {
      toast({
        description: t('completeSuccess')
      });
      void queryClient.invalidateQueries({
        queryKey: operationInstanceQueryKeys.detail('STACKTRACE_QA', instanceId)
      });
    },
    onError: message => {
      toast({
        variant: 'destructive',
        description: message
      });
    }
  });
  const handleComplete = useCallback(() => {
    if (!taskId) return;
    completeTestExecution();
  }, [completeTestExecution, taskId]);
  if (!executeTask) {
    return <FillScreen className="flex items-center justify-center">
        <Text variant="label" className="text-neutral-muted-on-fill">
          {t('noDetails')}
        </Text>
      </FillScreen>;
  }
  if (isConditionPending) {
    return <ViewportLoader />;
  }
  if (isConditionError || !condition) {
    return <FillScreen className="flex items-center justify-center">
        <VStack className="items-center gap-2 max-w-md text-center">
          <Text variant="label" className="text-neutral-on-fill">
            {t('missingContext')}
          </Text>
          <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
            {t('missingContextDescription')}
          </Text>
        </VStack>
      </FillScreen>;
  }
  return <FillScreen>
      <ExecuteTestCasesOperation conditionId={condition.conditionId} isCompleted={isCompleted} onComplete={handleComplete} isCompleting={isCompleting} />
    </FillScreen>;
}

/**
 * テストアサインデータタブ（Step1 `SelectTestCases` のテストケース複数選択）。
 *
 * 初回マウントで Step1 タスクを URL へ注入し、選択グリッドを描画する。完了後は
 * 次の InProgress な選択タスクが無いため detail タブへ戻す。Step2
 * `AssignExecutor`（アサイン先選択）は detail タブのタスクカード内でインライン
 * 完了するため、ここでは扱わない。
 */
function TestAssignmentDataTab() {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'qaOperationsCore.testAssignment.instance.data'
  });
  const params = Route.useParams();
  const {
    instanceId
  } = params;
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch();
  const {
    data: instance
  } = useSuspenseQueryOperationPackageInstance(instanceId);
  const steps = instance.operationSteps;

  // 初回マウント時、operationTaskInstanceId 未設定なら InProgress タスクを注入
  useEffect(() => {
    if (searchParams.operationTaskInstanceId) return;
    const defaultTask = resolveDefaultAssignmentTask(steps);
    if (defaultTask) {
      navigate({
        search: prev => ({
          ...prev,
          operationTaskInstanceId: defaultTask.task.id
        }),
        replace: true
      });
    }
  }, [searchParams.operationTaskInstanceId, steps, navigate]);
  const current = useMemo(() => findAssignmentTaskById(steps, searchParams.operationTaskInstanceId), [steps, searchParams.operationTaskInstanceId]);

  // 完了後: 次の InProgress タスクへ URL を進める（無ければ detail へ）
  const handleCompleted = useCallback(() => {
    if (!current) return;
    const next = resolveNextInProgressAssignmentTask(steps, current.task.id);
    if (next) {
      navigate({
        search: prev => ({
          ...prev,
          operationTaskInstanceId: next.task.id,
          freeText: undefined,
          pageIndex: 0,
          level: undefined,
          scope: undefined,
          status: undefined
        }),
        replace: true
      });
    } else {
      navigate({
        to: '/qa-operations-core/operation-packages/$id/patterns/$patternId/instances/$instanceId/detail',
        params
      });
    }
  }, [current, steps, navigate, params]);
  const onSearch = useCallback((searchUpdate: {
    freeText?: string;
    pageIndex?: number;
    level?: 'UNIT' | 'INTEGRATION' | 'SYSTEM' | 'UAT';
    scope?: 'UNDECIDED' | 'CHANGE_UNIT' | 'FULL';
    status?: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
  }) => {
    navigate({
      search: prev => ({
        ...prev,
        ...searchUpdate
      }),
      replace: true
    });
  }, [navigate]);
  if (!searchParams.operationTaskInstanceId) {
    return <ViewportLoader />;
  }
  if (!current) {
    return <FillScreen className="flex items-center justify-center">
        <Text variant="label" className="text-neutral-muted-on-fill">
          {t('noTask')}
        </Text>
      </FillScreen>;
  }
  const isCompleted = current.task.status === 'Completed';
  const pageIndex = searchParams.pageIndex ?? 0;
  return <FillScreen>
      <HStack className="h-full overflow-hidden">
        <SelectTestCasesOperation instanceId={instanceId} isCompleted={isCompleted} filters={{
        freeText: searchParams.freeText,
        level: searchParams.level,
        scope: searchParams.scope,
        status: searchParams.status
      }} pageIndex={pageIndex} onSearch={onSearch} onCompleted={handleCompleted} />
      </HStack>
    </FillScreen>;
}